import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import QuoteDialog from "@/components/QuoteDialog";
import { IMAGES } from "@/lib/site-data";

const navLinks = [
  { to: "/", label: "HOME" },
  { to: "/about", label: "ABOUT US" },
  { to: "/quality", label: "QUALITY" },
  { to: "/markets", label: "MARKETS" },
  { to: "/blog", label: "BLOG" },
  { to: "/contact", label: "CONTACT US" },
];

const products = [
  { to: "/products#red", label: "Fresh Red Onions" },
  { to: "/products#nashik", label: "Nashik Onions" },
  { to: "/products#pune", label: "Pune Onions" },
  { to: "/products#packed", label: "Packed Onions" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `text-[13px] font-semibold tracking-wide transition-colors duration-200 ${
      isActive ? "text-[#1e5ba8]" : "text-slate-700 hover:text-[#1e5ba8]"
    }`;

  return (
    <header
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
            <img src={IMAGES.logo} alt="EXIMXPRESS logo" className="h-20 w-auto object-contain shrink-0" />
            <div className="leading-tight hidden md:block">
              <div className="text-[10px] text-slate-500 tracking-[0.15em] font-medium">GLOBAL PRODUCE</div>
              <div className="text-[10px] text-slate-500 tracking-[0.15em] font-medium">TRUSTED EXPORTER</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.slice(0, 2).map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass} end={n.to === "/"} data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}>
                {n.label}
              </NavLink>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1 text-[13px] font-semibold tracking-wide text-slate-700 hover:text-[#1e5ba8] focus:outline-none"
                data-testid="nav-products-trigger"
              >
                PRODUCTS <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {products.map((p) => (
                  <DropdownMenuItem key={p.to} asChild data-testid={`dropdown-${p.label.replace(/\s+/g, "-").toLowerCase()}`}>
                    <Link to={p.to}>{p.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {navLinks.slice(2).map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => setQuoteOpen(true)}
              className="hidden md:inline-flex bg-[#1e5ba8] hover:bg-[#164582] text-white font-semibold px-5 py-2 rounded-md shadow-[0_4px_12px_rgba(30,91,168,0.25)]"
              data-testid="header-request-quote-btn"
            >
              REQUEST A QUOTE
            </Button>
            <button
              className="lg:hidden text-slate-700"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-slate-100 pt-3 flex flex-col gap-3" data-testid="mobile-nav">
            {navLinks.slice(0, 2).map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass} end={n.to === "/"} onClick={() => setMobileOpen(false)}>
                {n.label}
              </NavLink>
            ))}
            <NavLink to="/products" className={linkClass} onClick={() => setMobileOpen(false)}>PRODUCTS</NavLink>
            {navLinks.slice(2).map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass} onClick={() => setMobileOpen(false)}>
                {n.label}
              </NavLink>
            ))}
            <Button
              onClick={() => { setMobileOpen(false); setQuoteOpen(true); }}
              className="bg-[#1e5ba8] hover:bg-[#164582] text-white font-semibold w-fit"
              data-testid="mobile-request-quote-btn"
            >
              REQUEST A QUOTE
            </Button>
          </div>
        )}
      </div>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </header>
  );
}
